import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where, orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ReviewForm, ReviewWorkflowStatus, SelfReviewData, ManagerReviewData } from '../types';

const COL = 'reviewForms';
const LOG = 'epDiscretionLog';

// ─── Queries ──────────────────────────────────────────────────────────────────

export async function fetchReviewForm(id: string): Promise<ReviewForm | null> {
  const snap = await getDoc(doc(db, COL, id));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as ReviewForm) : null;
}

export async function fetchFormsForCycle(cycleId: string): Promise<ReviewForm[]> {
  const snap = await getDocs(
    query(collection(db, COL), where('cycleId', '==', cycleId), orderBy('revieweeName')),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewForm);
}

export async function fetchFormsForReviewee(revieweeId: string): Promise<ReviewForm[]> {
  const snap = await getDocs(
    query(collection(db, COL), where('revieweeId', '==', revieweeId), orderBy('createdAt', 'desc')),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewForm);
}

export async function fetchFormsForReviewer(reviewerId: string): Promise<ReviewForm[]> {
  const snap = await getDocs(
    query(collection(db, COL), where('reviewerId', '==', reviewerId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewForm);
}

/**
 * Get (or create) a ReviewForm for the given cycle + reviewee.
 * Used when employee opens SelfReviewForm for the first time.
 */
export async function getOrCreateReviewForm(
  cycleId: string,
  revieweeId: string,
  revieweeName: string,
  reviewerId: string,
): Promise<ReviewForm> {
  const snap = await getDocs(
    query(
      collection(db, COL),
      where('cycleId', '==', cycleId),
      where('revieweeId', '==', revieweeId),
    ),
  );
  if (!snap.empty) {
    return { id: snap.docs[0].id, ...snap.docs[0].data() } as ReviewForm;
  }
  // Create
  const ref = await addDoc(collection(db, COL), {
    cycleId,
    revieweeId,
    revieweeName,
    reviewerId,
    reviewerName: '',
    teamScorecardId: null,
    status: 'self_pending',
    selfReview: {},
    responses360Ids: [],
    managerReview: {},
    auditLog: [{ action: 'form_created', performedBy: revieweeId, timestamp: new Date().toISOString() }],
    createdAt: serverTimestamp(),
  });
  const newSnap = await getDoc(ref);
  return { id: ref.id, ...newSnap.data() } as ReviewForm;
}

// ─── Self-review ──────────────────────────────────────────────────────────────

export async function saveSelfReviewDraft(
  formId: string,
  selfReview: Partial<SelfReviewData>,
): Promise<void> {
  await updateDoc(doc(db, COL, formId), {
    selfReview,
    updatedAt: serverTimestamp(),
  });
}

export async function submitSelfReview(
  formId: string,
  selfReview: SelfReviewData,
  submittedBy: string,
): Promise<void> {
  const { arrayUnion } = await import('firebase/firestore');
  await updateDoc(doc(db, COL, formId), {
    selfReview: { ...selfReview, submittedAt: serverTimestamp() },
    status: 'self_submitted' as ReviewWorkflowStatus,
    updatedAt: serverTimestamp(),
    auditLog: arrayUnion({
      action: 'self_submitted',
      performedBy: submittedBy,
      timestamp: new Date().toISOString(),
    }),
  });
}

// ─── Manager review ───────────────────────────────────────────────────────────

export async function saveManagerReviewDraft(
  formId: string,
  managerReview: Partial<ManagerReviewData>,
): Promise<void> {
  await updateDoc(doc(db, COL, formId), {
    managerReview,
    updatedAt: serverTimestamp(),
  });
}

export async function submitManagerReview(
  formId: string,
  managerReview: ManagerReviewData,
  computedScore: number,
  submittedBy: string,
): Promise<void> {
  const { arrayUnion } = await import('firebase/firestore');
  await updateDoc(doc(db, COL, formId), {
    managerReview: { ...managerReview, submittedAt: serverTimestamp() },
    status: 'manager_submitted' as ReviewWorkflowStatus,
    computedScore,
    updatedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, COL, formId), {
    auditLog: arrayUnion({
      action: 'manager_submitted',
      performedBy: submittedBy,
      timestamp: new Date().toISOString(),
    }),
  });
}

// ─── Workflow transitions ─────────────────────────────────────────────────────

export async function transitionStatus(
  formId: string,
  newStatus: ReviewWorkflowStatus,
  performedBy: string,
  notes?: string,
): Promise<void> {
  const { arrayUnion } = await import('firebase/firestore');
  await updateDoc(doc(db, COL, formId), {
    status: newStatus,
    updatedAt: serverTimestamp(),
    auditLog: arrayUnion({
      action: `transition→${newStatus}`,
      performedBy,
      notes: notes ?? '',
      timestamp: new Date().toISOString(),
    }),
  });
}

// ─── Calibration ──────────────────────────────────────────────────────────────

export async function setCalibratedScore(
  formId: string,
  calibratedScore: number,
  setBy: string,
): Promise<void> {
  const { arrayUnion } = await import('firebase/firestore');
  await updateDoc(doc(db, COL, formId), {
    calibratedScore,
    updatedAt: serverTimestamp(),
    auditLog: arrayUnion({
      action: 'calibration_set',
      performedBy: setBy,
      notes: `calibrated score set to ${calibratedScore}`,
      timestamp: new Date().toISOString(),
    }),
  });
}

// ─── Discretion Adjustment (immutable log) ────────────────────────────────────

export async function logDiscretionAdjustment(
  formId: string,
  revieweeId: string,
  previousScore: number,
  delta: number,
  reason: string,
  adjustedBy: string,
  maxDelta: number,
  scale: number = 5,
): Promise<void> {
  const { arrayUnion } = await import('firebase/firestore');
  const clampedDelta = Math.max(-maxDelta, Math.min(maxDelta, delta));
  const newScore = Math.max(1, Math.min(scale, previousScore + clampedDelta));

  // 1. Write immutable log entry
  await addDoc(collection(db, LOG), {
    reviewFormId: formId,
    revieweeId,
    adjustedBy,
    previousScore,
    newScore,
    delta: clampedDelta,
    reason,
    timestamp: serverTimestamp(),
  });

  // 2. Update form
  await updateDoc(doc(db, COL, formId), {
    discretionAdjustment: clampedDelta,
    finalScore: newScore,
    updatedAt: serverTimestamp(),
    auditLog: arrayUnion({
      action: 'discretion_applied',
      performedBy: adjustedBy,
      notes: `delta=${clampedDelta}, reason="${reason}"`,
      timestamp: new Date().toISOString(),
    }),
  });
}

// ─── Approval & sign-off ──────────────────────────────────────────────────────

export async function approveReviewForm(formId: string, approvedBy: string): Promise<void> {
  const { arrayUnion } = await import('firebase/firestore');
  await updateDoc(doc(db, COL, formId), {
    status: 'approved' as ReviewWorkflowStatus,
    approvedBy,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    auditLog: arrayUnion({
      action: 'approved',
      performedBy: approvedBy,
      timestamp: new Date().toISOString(),
    }),
  });
}

export async function signOffReviewForm(formId: string, signedOffBy: string): Promise<void> {
  const { arrayUnion } = await import('firebase/firestore');
  await updateDoc(doc(db, COL, formId), {
    status: 'signed_off' as ReviewWorkflowStatus,
    signedOffBy,
    signedOffAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    auditLog: arrayUnion({
      action: 'signed_off',
      performedBy: signedOffBy,
      timestamp: new Date().toISOString(),
    }),
  });
}

export async function linkTeamScorecard(formId: string, scorecardId: string): Promise<void> {
  await updateDoc(doc(db, COL, formId), {
    teamScorecardId: scorecardId,
    updatedAt: serverTimestamp(),
  });
}

/** Permanently delete a review form. Only use for forms that have not been submitted. */
export async function deleteReviewForm(formId: string): Promise<void> {
  await deleteDoc(doc(db, COL, formId));
}
